import {
  OreButton,
  OreCheckbox,
  OrePagination,
  OreStack,
  OreTable,
  OreTableActions,
  OreTableBody,
  OreTableCaption,
  OreTableCell,
  OreTableContainer,
  OreTableHead,
  OreTableRow,
  OreText,
  OreToolbar,
  SvgArrowWidgetRight,
  SvgDownload,
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { homeTranslation } from '@/Home/ui/homeTranslation'
import { InvoiceModel } from '@/Invoices/domain'
import { useInvoice } from '@/Invoices/ui/context'
import { useBatchCheckboxes } from '@/Invoices/ui/hooks'
import { invoicesTranslation } from '@/Invoices/ui/invoicesTranslation'
import { InvoiceRow } from '@/Invoices/ui/molecules'
import { FilterModel, daysAgo, edenredProducts, getToday } from '@/Shared/domain'
import { TableHeadersModel } from '@/Shared/domain/models/TableHeadersModel'
import { TableSearcher } from '@/Shared/ui/TableSearcher'
import { navigationRoutes } from '@/Shared/ui/routers'
import { tablePagination } from '@/Shared/ui/sharedTranslation'
import { TransactionFilterLocals } from '@/Transactions/domain/TransactionFilterLocals'
import { createApiTransactionRepository } from '@/Transactions/infrastructure/ApiTransactionRepository'
import { transactionsTranslation } from '@/Transactions/ui/transactionsTranslation'
import { UserType } from '@/User/domain'
import { useUserData } from '@/User/ui/context'

interface IProps {
  isHome?: boolean
}

export const InvoicesList = ({ isHome }: IProps): JSX.Element => {
  const [availableMultipleTransactions, setAvailableMultipleTransactions] =
    useState(false)

  const [availableMultipleInvoices, setAvailableMultipleInvoices] = useState(false)

  const { merchant } = useUserData()
  const isMasterGroup = merchant.isMasterGroup
  const isChild = merchant.isChild
  const isParent = merchant.isParent
  const isParentCommercial = merchant.isParentCommercial
  const isChildCommercial = merchant.isChildCommercial
  const isParentWithHardDependency = merchant.isParentWithHardDependency
  const isIndependent = merchant.isIndependent
  const isNursery = merchant.isNursery

  const firstUseProfile =
    isChildCommercial || isParentCommercial || isParentWithHardDependency

  const { t } = useTranslation()
  // Datos de locales (para el multiselect localCode) obtenidos de forma directa sin depender del TransactionProvider.
  const [localsFilterData, setLocalsFilterData] = useState<TransactionFilterLocals[]>([])
  const [headers, setHeaders] = useState<TableHeadersModel[]>([])

  const {
    invoices,
    search,
    count,
    onPageChange,
    page,
    numberOfRecordsPerPage,
    downloadAllPdfs,
    downloadAllTransactions,
  } = useInvoice()

  const { userType } = useUserData()

  const navigate = useNavigate()

  const {
    handleSelectAllClick,
    selectedStoredItems,
    handleSelect,
    indeterminate,
    massiveSelect,
    selected,
    init,
  } = useBatchCheckboxes()

  // const showMerchantColumn = isParent || isMasterGroup

  const invoicesGuarderiaHeaders: TableHeadersModel[] = [
    {
      key: 'creationDate',
      label: invoicesTranslation.table.header.creationDate,
      size: 'auto',
    },
    ...(!isChild && isNursery && !isParentWithHardDependency && !isIndependent
      ? [
          {
            key: 'merchantName',
            label: invoicesTranslation.table.header.nursery,
            size: 'auto' as const,
          },
        ]
      : []),
    {
      key: 'number',
      label: invoicesTranslation.table.header.number,
      size: 'auto',
    },
    {
      key: 'status',
      label: invoicesTranslation.table.header.status,
      size: 'auto',
    },
    {
      key: 'reimburcementQuantity',
      label: invoicesTranslation.table.header.nTransacciones,
      size: 'auto',
    },
    {
      key: 'totalAmount',
      label: invoicesTranslation.table.header.totalAmount,
      size: 'auto',
    },
    {
      key: 'actions',
      label: invoicesTranslation.table.header.actions,
      size: 'auto',
    },
  ]

  const invoiceHeaders: TableHeadersModel[] = [
    ...(!isIndependent && !isHome
      ? [
          {
            key: 'merchantName',
            label: invoicesTranslation.table.header.local,
            size: 'auto' as const,
          },
        ]
      : []),
    ...(firstUseProfile && !isHome
      ? []
      : [
          {
            key: 'creationDate',
            label: invoicesTranslation.table.header.creationDate,
            size: 'auto' as const,
          },
        ]),

    {
      key: 'number',
      label: invoicesTranslation.table.header.number,
      size: 'auto',
    },
    {
      key: 'status',
      label: invoicesTranslation.table.header.status,
      size: 'auto',
    },
    {
      key: 'reimburcementDates',
      label: invoicesTranslation.table.header.reimburcementDates,
      size: 'auto',
    },
    ...((!merchant.hasFlatRateCommission
      ? [
          {
            key: 'reimburcementQuantity',
            label: invoicesTranslation.table.header.reimburcementQuantity,
            size: 'auto',
          },
          {
            key: 'reimburcementAmount',
            label: invoicesTranslation.table.header.reimburcementAmount,
            size: 'auto',
          },
        ]
      : []) as TableHeadersModel[]),
    {
      key: 'totalAmount',
      label: invoicesTranslation.table.header.totalAmount,
      size: 'auto',
    },
    {
      key: 'actions',
      label: invoicesTranslation.table.header.actions,
      size: 'auto',
    },
  ]

  const YEAR = 365

  const filters: FilterModel[] = [
    {
      id: 'beginDate',
      label: t(invoicesTranslation.table.filters.beginDate),
      type: 'date',
      maxDate: getToday(),
      minDate: daysAgo(YEAR * 4),
    },
    {
      id: 'endDate',
      label: t(invoicesTranslation.table.filters.endDate),
      type: 'date',
      // endDate toma beginDate como minimo dinamico para impedir seleccionar fechas anteriores.
      minDate: 'beginDate',
      // Mensaje equivalente al test de TransactionFilter: endDate debe ser >= beginDate.
      dateMinErrorMessage: t(
        transactionsTranslation.table.filters.errors.endDateBeforeBeginDate,
      ),
      maxDate: getToday(),
    },
    {
      id: 'invoiceStatus',
      label: t(invoicesTranslation.table.filters.status),
      type: 'select',
      placeholder: t(invoicesTranslation.table.filters.statusPlaceholder),
      options: [
        { label: t(invoicesTranslation.table.body.state.paid), value: '4' },
        { label: t(invoicesTranslation.table.body.state.pending), value: '1' },
      ],
    },
  ]
  // Añadimos filtros específicos de restaurante (estado factura y locales)
  if (userType === UserType.RESTAURANT) {
    // Locales solo cuando NO es parent
    if (isMasterGroup || (isParent && !isChild && isParentCommercial)) {
      filters.push({
        id: 'localCode',
        label: t(transactionsTranslation.table.filters.local.label),
        type: 'select',
        placeholder: t(transactionsTranslation.table.filters.local.placeholder),
        options: (localsFilterData || []).map(local => ({
          label: local.name,
          value: local.code.toString(),
        })),
      })
    }
  }
  if (
    userType === UserType.NURSERY &&
    ((isParent && !isChild) || isMasterGroup || isParentCommercial) &&
    !isParentWithHardDependency
  ) {
    filters.splice(2, 0, {
      id: 'localCode',
      label: t(invoicesTranslation.table.header.nursery),
      type: 'select',
      requiresDateRange: false,
      placeholder: t(transactionsTranslation.table.filters.product.placeholder),
      options: (localsFilterData || []).map(local => ({
        label: local.name,
        value: local.code.toString(),
      })),
    })
  }

  // Limpiar el sessionStorage al montar el componente
  useEffect(() => {
    sessionStorage.removeItem('activeInvoices')
  }, [])

  useEffect(() => {
    if (invoices) init(invoices)
  }, [invoices])

  useEffect(() => {
    if (merchant.isNursery) {
      const baseHeaders =
        isHome || isParentWithHardDependency
          ? invoicesGuarderiaHeaders.filter(h => h.key !== 'merchantName')
          : invoicesGuarderiaHeaders

      setHeaders(baseHeaders)
    } else {
      setHeaders(invoiceHeaders)
    }
  }, [merchant, isHome])

  // Cargar locales para el multiselect (solo si es restaurante y aún no se han cargado)
  useEffect(() => {
    if (userType === UserType.RESTAURANT && localsFilterData.length === 0) {
      const repo = createApiTransactionRepository()
      repo
        .GetTransactionLocalsFilter()
        .then(resp => {
          if (resp && resp.data) {
            setLocalsFilterData(resp.data)
          }
        })
        .catch(() => {
          // Silenciar error; si falla se queda vacío y simplemente no habrá opciones.
        })
    }
    if (userType === UserType.NURSERY && localsFilterData.length === 0) {
      const repo = createApiTransactionRepository()
      repo
        .GetTransactionLocalsFilter()
        .then(resp => {
          if (resp && resp.data) {
            setLocalsFilterData(resp.data)
          }
        })
        .catch(() => {
          // Silenciar error; si falla se queda vacío y simplemente no habrá opciones.
        })
    }
  }, [userType, localsFilterData.length])

  useEffect(() => {
    const hasInvoiceAvailable = selectedStoredItems.some(
      s => s.hasInvoiceAvailable && s.isInvoiceDownloadeable,
    )
    const hasTransactionAvailable = selectedStoredItems.some(
      s => s.hasTransactionAvailable,
    )

    setAvailableMultipleInvoices(hasInvoiceAvailable)
    setAvailableMultipleTransactions(hasTransactionAvailable)
  }, [selected])

  // Mostrar como máximo 8 filas por página
  const MAX_ROWS = 8
  const visibleInvoices = invoices
    ? invoices.slice(0, Math.min(MAX_ROWS, numberOfRecordsPerPage ?? MAX_ROWS))
    : []

  return (
    <OreStack placeContent="stretch">
      <OreTableContainer>
        {!isHome ? (
          <TableSearcher
            handleSearch={search}
            filters={filters}
            searchPlaceholder={t(invoicesTranslation.table.filters.searchPlaceholder)}
          />
        ) : null}
        <OreTable>
          {isHome ? (
            <OreTableCaption title={t(homeTranslation.invoices.table.title)} />
          ) : null}
          <OreTableHead valign="middle">
            <OreTableRow hover={false}>
              {!isHome ? (
                <OreTableCell as="th">
                  <OreCheckbox
                    name="check"
                    indeterminate={indeterminate}
                    onChange={e => handleSelectAllClick(e)}
                    disabled={!invoices || invoices.length === 0}
                    checked={massiveSelect}
                  />
                </OreTableCell>
              ) : null}
              {headers.map((header: TableHeadersModel) => (
                <OreTableCell as="th" key={header.key} size={header.size}>
                  <span style={{ whiteSpace: 'pre-line', display: 'flex' }}>
                    {header.label && t(header.label)}
                  </span>
                </OreTableCell>
              ))}
            </OreTableRow>
          </OreTableHead>
          <OreTableBody valign="top">
            {visibleInvoices && visibleInvoices.length > 0
              ? visibleInvoices.map((invoice: InvoiceModel, index: number) => {
                  return (
                    <InvoiceRow
                      key={`p_${index}`}
                      invoice={invoice}
                      selected={
                        selected.find(s => s.id === invoice.invoiceId)?.active ?? false
                      }
                      handleSelect={handleSelect}
                      isHome={isHome}
                    />
                  )
                })
              : null}
          </OreTableBody>
        </OreTable>
        {!invoices || invoices.length === 0 ? (
          <OreTableActions>
            <OreText size="text-sm" tone="neutral-600" data-testid="noInvoices">
              {t(invoicesTranslation.table.noInvoices)}
            </OreText>
          </OreTableActions>
        ) : isHome ? (
          <OreTableActions>
            <OreButton
              category="highlight"
              icon={<SvgArrowWidgetRight />}
              iconAlign="right"
              onClick={() =>
                navigate(navigationRoutes.invoices, {
                  state: {
                    from: navigationRoutes.home,
                  },
                })
              }
            >
              {t(homeTranslation.invoices.table.viewMore)}
            </OreButton>
          </OreTableActions>
        ) : null}
        {!isHome && count > 0 ? (
          <OreTableActions>
            <OrePagination
              displayPageButtons
              count={count}
              labelNextPage={t(tablePagination.nextPageLabel)}
              labelPreviousPage={t(tablePagination.previousPageLabel)}
              onPageChange={onPageChange}
              page={page}
              rowsPerPage={numberOfRecordsPerPage}
              text={t(tablePagination.pageOf)}
              legend={t(tablePagination.rowsShowing)}
            />
          </OreTableActions>
        ) : null}
      </OreTableContainer>
      {selectedStoredItems.length > 0 && (
        <OreToolbar
          first_section={
            <>
              <OreText size="text-xs">
                {selectedStoredItems.length}{' '}
                {t(invoicesTranslation.toolbar.itemsSelected)}
              </OreText>
            </>
          }
          second_section={
            <>
              {availableMultipleInvoices ? (
                <OreButton icon={<SvgDownload />} onClick={() => downloadAllPdfs()}>
                  {t(invoicesTranslation.toolbar.downloadInvoices)}
                </OreButton>
              ) : null}
              {availableMultipleTransactions ? (
                <OreButton
                  icon={<SvgDownload />}
                  onClick={() =>
                    downloadAllTransactions(
                      userType === UserType.NURSERY
                        ? edenredProducts.ticketGuarderia
                        : edenredProducts.ticketRestaurant,
                    )
                  }
                >
                  {t(invoicesTranslation.toolbar.downloadTransactions)}
                </OreButton>
              ) : null}
            </>
          }
          sticky
        />
      )}
    </OreStack>
  )
}
